import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';

import { ReactComponent as CheckIcon } from '@/assets/icons/check-circle.svg';
import { ReactComponent as NeedsActionIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as UpdateIcon } from '@/assets/icons/refresh-icon.svg';
import { ReactComponent as DeclineIcon } from '@/assets/icons/x-circle.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap } from '@/components/Table';
import { FreelancerInvoiceSubmissionResource } from '@/types/resources';
import { InvoiceGenerationStatus } from '@/utils/enums';

import InvoiceActionModal from './InvoiceActionModal';
import InvoiceApprovalModal from './InvoiceApprovalModal';
import InvoiceRejectionModal from './InvoiceRejectionModal';
import InvoiceUpdateModal from './InvoiceUpdateModal';

const queryKey = 'freelancer_invoice_submissions_approval';

type OpenModal = 'approve' | 'reject' | 'requestChanges' | 'update' | null;

interface Props {
  submission: FreelancerInvoiceSubmissionResource;
}

/** Row actions dropdown plus the modals each action opens. */
const InvoiceRowActions: FC<Props> = ({ submission }) => {
  const [openModal, setOpenModal] = useState<OpenModal>(null);

  const closeModal = () => setOpenModal(null);

  return (
    <>
      <ActionsWrap>
        <ActionsDropdown>
          {submission.status === InvoiceGenerationStatus.Finances_Rejected ? (
            <ActionsDropdownButton
              as="button"
              onClick={() => setOpenModal('update')}
            >
              <UpdateIcon />
              Update
            </ActionsDropdownButton>
          ) : (
            <>
              <ActionsDropdownButton
                as="button"
                onClick={() => setOpenModal('approve')}
              >
                <CheckIcon />
                Approve
              </ActionsDropdownButton>
              <ActionsDropdownButton
                as="button"
                onClick={() => setOpenModal('requestChanges')}
              >
                <NeedsActionIcon />
                Request Changes
              </ActionsDropdownButton>
              <ActionsDropdownButton
                as="button"
                onClick={() => setOpenModal('reject')}
              >
                <DeclineIcon />
                Reject
              </ActionsDropdownButton>
            </>
          )}
        </ActionsDropdown>
      </ActionsWrap>

      <AnimatePresence>
        {openModal === 'approve' && (
          <InvoiceApprovalModal
            submission={submission}
            queryKey={queryKey}
            onClose={closeModal}
          />
        )}
        {openModal === 'reject' && (
          <InvoiceRejectionModal
            submissionId={submission.id}
            queryKey={queryKey}
            onClose={closeModal}
          />
        )}
        {openModal === 'requestChanges' && (
          <InvoiceActionModal
            submissionId={submission.id}
            queryKey={queryKey}
            onClose={closeModal}
          />
        )}
        {openModal === 'update' && (
          <InvoiceUpdateModal
            submission={submission}
            queryKey={queryKey}
            onClose={closeModal}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default InvoiceRowActions;
